import { useState, useEffect, useRef } from 'react';
import { getTickets, updateTicket, getTicketMessages, sendTicketMessage } from '../services/api';

const STATUS_COLORS = { open: 'badge-danger', in_progress: 'badge-warning', resolved: 'badge-success', closed: 'badge-grey' };

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState('open');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (selected) {
      loadMessages(selected.id);
      pollRef.current = setInterval(() => loadMessages(selected.id), 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [selected]);

  const load = async () => {
    setLoading(true);
    try { setTickets(await getTickets()); } catch (e) {}
    setLoading(false);
  };

  const loadMessages = async (id) => {
    try { setMessages(await getTicketMessages(id)); } catch (e) {}
  };

  const open = (t) => { setSelected(t); setStatus(t.status); setReply(''); };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await sendTicketMessage(selected.id, { message: reply.trim(), status });
      setReply('');
      await loadMessages(selected.id);
      // refresh ticket list to update status
      const updated = await getTickets();
      setTickets(updated);
      const refreshed = updated.find(t => t.id === selected.id);
      if (refreshed) setSelected(refreshed);
    } catch (e) { alert('Failed to send'); }
    setSending(false);
  };

  const changeStatus = async (newStatus) => {
    setStatus(newStatus);
    try {
      await updateTicket(selected.id, { status: newStatus });
      const updated = await getTickets();
      setTickets(updated);
      const refreshed = updated.find(t => t.id === selected.id);
      if (refreshed) setSelected({ ...refreshed });
    } catch (e) {}
  };

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 80px)' }}>
      {/* Ticket List */}
      <div style={{ width: selected ? 380 : '100%', flexShrink: 0, overflowY: 'auto' }}>
        <div className="page-header">
          <h1>Support Tickets</h1>
          <button className="refresh-btn" onClick={load}>🔄 Refresh</button>
        </div>
        <div style={{ padding: '0 16px 12px' }}>
          <input
            placeholder="🔍 Search by ID, user, subject..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <div className="card">
          {loading ? <div className="loading">Loading...</div> : tickets.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>No tickets yet</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>ID</th><th>User</th><th>Subject</th><th>Status</th><th>Msgs</th><th>Date</th><th></th></tr>
                </thead>
                <tbody>
                  {tickets
                    .filter(t => {
                      if (!search.trim()) return true;
                      const q = search.toLowerCase();
                      return String(t.id).includes(q) ||
                             (t.user_name || '').toLowerCase().includes(q) ||
                             (t.subject || '').toLowerCase().includes(q);
                    })
                    .map(t => (
                    <tr key={t.id} style={{ background: selected?.id === t.id ? '#f0f4ff' : '' }}>
                      <td>#{t.id}</td>
                      <td>{t.user_name}</td>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                      <td><span className={`badge ${STATUS_COLORS[t.status] || 'badge-grey'}`}>{t.status}</span></td>
                      <td>{t.message_count ?? 0}</td>
                      <td>{t.created_at?.substring(0, 10)}</td>
                      <td><button className="btn btn-sm btn-primary" onClick={() => open(t)}>💬 Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Chat Panel */}
      {selected && (
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Header */}
          <div className="card-header" style={{ flexShrink: 0 }}>
            <div>
              <h2 style={{ margin: 0 }}>🎫 #{selected.id}: {selected.subject}</h2>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
                From: <strong>{selected.user_name}</strong> ({selected.user_email}) • {selected.created_at?.substring(0, 10)}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                value={status}
                onChange={e => changeStatus(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <button className="btn btn-danger btn-sm" onClick={() => setSelected(null)}>✕</button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', background: '#f9f9f9' }}>
            {messages.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#aaa', marginTop: 40 }}>No messages yet</p>
            ) : messages.map(m => {
              const isAdmin = m.sender_role === 'admin';
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                  <div style={{
                    maxWidth: '70%', padding: '10px 14px', borderRadius: 16,
                    background: isAdmin ? '#4f46e5' : '#fff',
                    color: isAdmin ? '#fff' : '#333',
                    border: isAdmin ? 'none' : '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}>
                    {!isAdmin && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: m.sender_role === 'worker' ? '#0d9488' : '#7c3aed', marginBottom: 3 }}>
                        {m.sender_name} {m.sender_role === 'worker' ? '(Partner)' : '(Customer)'}
                      </div>
                    )}
                    <div style={{ fontSize: 14 }}>{m.message}</div>
                    <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: isAdmin ? 'right' : 'left' }}>
                      {m.created_at?.substring(11, 16)}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Input */}
          {(status === 'resolved' || status === 'closed') ? (
            <div style={{ padding: 12, textAlign: 'center', color: '#999', background: '#f5f5f5', borderTop: '1px solid #eee', fontSize: 13 }}>
              🔒 Ticket is {status}
            </div>
          ) : (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #eee', display: 'flex', gap: 8, flexShrink: 0 }}>
              <textarea
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={2}
                placeholder="Type reply... (Enter to send)"
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8, resize: 'none', fontSize: 14 }}
              />
              <button
                className="btn btn-primary"
                onClick={send}
                disabled={sending || !reply.trim()}
                style={{ alignSelf: 'flex-end', padding: '8px 18px' }}
              >
                {sending ? '...' : '➤ Send'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
